import type { OptionContract } from "./types";

export interface StrikeRow {
  strike: number;
  call?: OptionContract;
  put?: OptionContract;
}

export type OptionPriceDirection = "up" | "down" | "flat" | null;
const OPTION_PRICE_EPSILON = 0.00001;

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function optionPriceDifference(current: number, previous: number): number {
  const difference = current - previous;
  return Math.abs(difference) < OPTION_PRICE_EPSILON ? 0 : difference;
}

export function optionDailyChange(
  contract: OptionContract | undefined,
): number | null {
  if (!isFiniteNumber(contract?.last)) return null;
  if (isFiniteNumber(contract.previous_close)) {
    return optionPriceDifference(contract.last, contract.previous_close);
  }
  if (isFiniteNumber(contract.change)) return contract.change;

  const percentChange = contract.percent_change;
  if (!isFiniteNumber(percentChange) || percentChange === -1) return null;

  const previousClose = contract.last / (1 + percentChange);
  return Number.isFinite(previousClose) ? contract.last - previousClose : null;
}

export function optionDailyPercentChange(
  contract: OptionContract | undefined,
): number | null {
  if (!isFiniteNumber(contract?.last)) return null;
  if (isFiniteNumber(contract.previous_close)) {
    if (contract.previous_close === 0) return null;
    return (
      optionPriceDifference(contract.last, contract.previous_close) /
      contract.previous_close
    );
  }
  if (isFiniteNumber(contract.percent_change)) return contract.percent_change;
  if (!isFiniteNumber(contract.change)) return null;

  const previousClose = contract.last - contract.change;
  if (previousClose === 0) return null;

  const percentChange = contract.change / previousClose;
  return Number.isFinite(percentChange) ? percentChange : null;
}

export function optionPriceDirection(
  contract: OptionContract | undefined,
): OptionPriceDirection {
  const change = optionDailyChange(contract);
  if (change === null) return null;
  if (change > 0) return "up";
  if (change < 0) return "down";
  return "flat";
}

export function optionPriceRoi(
  contract: OptionContract | undefined,
  underlyingPrice: number | null,
): number | null {
  if (
    !isFiniteNumber(contract?.last) ||
    !isFiniteNumber(underlyingPrice) ||
    underlyingPrice <= 0
  ) {
    return null;
  }

  return contract.last / underlyingPrice;
}

export function groupChain(contracts: OptionContract[]): StrikeRow[] {
  const rows = new Map<number, StrikeRow>();
  for (const contract of contracts) {
    const row = rows.get(contract.strike) ?? { strike: contract.strike };
    row[contract.type] = contract;
    rows.set(contract.strike, row);
  }
  return [...rows.values()].sort((a, b) => a.strike - b.strike);
}
